import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["global", "children", "theme"];
import cactusTheme from '@repay/cactus-theme';
import PropTypes from 'prop-types';
import React from 'react';
import * as styledComponents from 'styled-components';
import { textStyle } from '../helpers/theme';
var _ref = styledComponents,
    createGlobalStyle = _ref.createGlobalStyle;
var DebugStyle = /*#__PURE__*/createGlobalStyle([":root{--reach-dialog:1;}"]);
var GlobalStyle = /*#__PURE__*/createGlobalStyle(["html,body,button,input,select,textarea{font-weight:400;color:", ";font-style:normal;font-stretch:normal;letter-spacing:normal;}html,body,input,select,button,textarea{", ";font-family:", ";}html,body{margin:0;}small{", "}h1{", ";}h2{", ";}h3{", ";}h4,h5,h6{", ";}"], function (p) {
  return p.theme.colors.darkestContrast;
}, function (p) {
  return textStyle(p.theme, 'body');
}, function (p) {
  return p.theme.font;
}, function (p) {
  return textStyle(p.theme, 'small');
}, function (p) {
  return textStyle(p.theme, 'h1');
}, function (p) {
  return textStyle(p.theme, 'h2');
}, function (p) {
  return textStyle(p.theme, 'h3');
}, function (p) {
  return textStyle(p.theme, 'h4');
});
var shouldCheckTheme = process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test';

var checkThemeProperties = function checkThemeProperties(theme) {
  if (theme.colors.lightCallToAction === undefined) {
    console.warn("You are using an outdated version of @repay/cactus-theme. Some features won't be available in @repay/cactus-web with this version. Please upgrade to @repay/cactus-theme >= 3.0.0.");
  }

  shouldCheckTheme = false;
};

export var StyleProvider = function StyleProvider(props) {
  var global = props.global,
      children = props.children,
      providedTheme = props.theme,
      themeProviderProps = _objectWithoutProperties(props, _excluded);

  shouldCheckTheme && checkThemeProperties(providedTheme || cactusTheme);
  var theme = providedTheme ? providedTheme : cactusTheme;
  return /*#__PURE__*/React.createElement(styledComponents.ThemeProvider, Object.assign({
    theme: theme
  }, themeProviderProps), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DebugStyle, null), global && /*#__PURE__*/React.createElement(GlobalStyle, null), children));
}; // PropTypes declarations of some recurring types so they can be re-used.

var textStyleObj = PropTypes.shape({
  fontSize: PropTypes.string.isRequired,
  lineHeight: PropTypes.string.isRequired
}).isRequired;
var textStylesObj = PropTypes.shape({
  tiny: textStyleObj,
  small: textStyleObj,
  body: textStyleObj,
  h4: textStyleObj,
  h3: textStyleObj,
  h2: textStyleObj,
  h1: textStyleObj
}).isRequired;
var colorStyleObj = PropTypes.shape({
  color: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired
}).isRequired;
var statusObj = PropTypes.shape({
  success: PropTypes.string.isRequired,
  warning: PropTypes.string.isRequired,
  error: PropTypes.string.isRequired
}).isRequired;

var generateArrayValidator = function generateArrayValidator(expectedLength) {
  return PropTypes.arrayOf(function (propValue, key, componentName, _, propFullName) {
    if (propValue.length !== expectedLength) {
      return new Error("Invalid prop `".concat(propFullName, "` supplied to `").concat(componentName, "`. ").concat(propFullName.substring(0, propFullName.length - 3), " must be an array of length ").concat(expectedLength, ", but it is length ").concat(propValue.length, "."));
    }

    var type = typeof propValue[key];

    if (type !== 'number') {
      return new Error("Invalid prop `".concat(propFullName, "` of type `").concat(type, "` supplied to `").concat(componentName, "`, expected `number`."));
    }

    return null;
  }).isRequired;
};

StyleProvider.propTypes = {
  global: PropTypes.bool,
  // @ts-ignore
  theme: PropTypes.shape({
    colors: PropTypes.shape({
      base: PropTypes.string.isRequired,
      baseText: PropTypes.string.isRequired,
      callToAction: PropTypes.string.isRequired,
      callToActionText: PropTypes.string.isRequired,
      lightCallToAction: PropTypes.string.isRequired,
      lightContrast: PropTypes.string.isRequired,
      mediumContrast: PropTypes.string.isRequired,
      darkContrast: PropTypes.string.isRequired,
      darkestContrast: PropTypes.string.isRequired,
      white: PropTypes.string.isRequired,
      lightGray: PropTypes.string.isRequired,
      mediumGray: PropTypes.string.isRequired,
      darkGray: PropTypes.string.isRequired,
      success: PropTypes.string.isRequired,
      warning: PropTypes.string.isRequired,
      error: PropTypes.string.isRequired,
      successLight: PropTypes.string.isRequired,
      warningLight: PropTypes.string.isRequired,
      errorLight: PropTypes.string.isRequired,
      successMedium: PropTypes.string.isRequired,
      warningMedium: PropTypes.string.isRequired,
      errorMedium: PropTypes.string.isRequired,
      successDark: PropTypes.string.isRequired,
      warningDark: PropTypes.string.isRequired,
      errorDark: PropTypes.string.isRequired,
      status: PropTypes.shape({
        background: statusObj,
        avatar: statusObj
      }).isRequired
    }).isRequired,
    space: generateArrayValidator(8),
    fontSizes: generateArrayValidator(7),
    mobileFontSizes: generateArrayValidator(7),
    iconSizes: generateArrayValidator(4),
    textStyles: textStylesObj,
    mobileTextStyles: textStylesObj,
    colorStyles: PropTypes.shape({
      base: colorStyleObj,
      callToAction: colorStyleObj,
      standard: colorStyleObj,
      lightContrast: colorStyleObj,
      darkestContrast: colorStyleObj,
      success: colorStyleObj,
      error: colorStyleObj,
      warning: colorStyleObj,
      disable: colorStyleObj,
      lightCallToAction: colorStyleObj,
      successLight: colorStyleObj,
      errorLight: colorStyleObj,
      warningLight: colorStyleObj,
      successMedium: colorStyleObj,
      errorMedium: colorStyleObj,
      warningMedium: colorStyleObj,
      successDark: colorStyleObj,
      errorDark: colorStyleObj,
      warningDark: colorStyleObj
    }).isRequired,
    border: PropTypes.oneOf(['thin', 'thick']).isRequired,
    shape: PropTypes.oneOf(['square', 'intermediate', 'round']).isRequired,
    font: PropTypes.string.isRequired,
    boxShadows: PropTypes.bool.isRequired
  })
};
export default StyleProvider;